import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import HeroWave from '@/components/HeroWave';

const GRAD = '#5048E5';
const FONT = 'Plus Jakarta Sans, sans-serif';
const HEAD = 'Outfit, sans-serif';

export interface ServiceFinalBannerProps {
  heading: string;
  sub: string;
  ctaText: string;
  to?: string;
}

/**
 * Shared closing CTA used at the bottom of every service page. Sits directly
 * on the page's bottom HeroWave background — no boxed banner. The wave fades
 * behind the text so contrast passes WCAG AA in both light and dark modes.
 * Uses the same foreground/muted-foreground tokens the rest of the site uses,
 * which are contrast-checked for both themes.
 */
export default function ServiceFinalBanner({
  heading, sub, ctaText, to = '/get-started',
}: ServiceFinalBannerProps) {
  return (
    <section
      className="relative overflow-hidden py-20 md:py-24"
      style={{ background: 'hsl(var(--background))' }}
    >
      {/* Wave background, faded top and bottom so the CTA text sits on a calm
          area of the artwork with plenty of contrast against the section
          background token. */}
      {/* fadeTop is intentionally deep (≈60) so the wave emerges gradually
          from the section above — a shallow fadeTop caused a visible
          horizontal seam where the artwork began. */}
      <HeroWave fadeTop={60} fadeBottom={78} opacity={0.24} edgeFade />
      <div className="relative mx-auto max-w-2xl px-6 text-center">
        <h2
          className="mb-4 text-3xl font-bold md:text-4xl"
          style={{ fontFamily: HEAD, color: 'hsl(var(--foreground))', lineHeight: 1.1 }}
        >
          {heading}
        </h2>
        <p
          className="mx-auto mb-8 max-w-xl text-base leading-relaxed"
          style={{ fontFamily: FONT, color: 'hsl(var(--muted-foreground))' }}
        >
          {sub}
        </p>
        <Link
          to={to}
          className="btn-glow inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-bold transition-all"
          style={{ background: GRAD, color: '#ffffff', fontFamily: FONT }}
        >
          {ctaText} <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}
